import type { ScmAuthApi } from '@backstage/integration-react';

const GITHUB_URL = 'https://github.com';
const GRAPHQL_URL = 'https://api.github.com/graphql';

/**
 * Runs a query against GitHub's GraphQL API with the signed-in user's own
 * GitHub token (the `repo` scope the home cards already ask for). Throws when
 * there is no token, on an HTTP error, and when GitHub answers with errors
 * and no data.
 *
 * Errors alongside data are partial answers, not failures: people sign in
 * through the portal's GitHub App, whose token can't read every field (check
 * results need their own permission). GitHub then returns the rest, with the
 * fields it couldn't read set to null, and callers must allow for that.
 */
/** A GraphQL error; `path` names the field it's about, e.g.
 * `['search', 'nodes', 2, 'commits', 'nodes', 0, 'commit', 'statusCheckRollup']`. */
export type GraphqlError = { message: string; path?: (string | number)[] };

/**
 * Like githubGraphql, but also returns the errors that came with the data, so
 * a caller can tell a field it may not read from one that is really empty.
 */
export const githubGraphqlPartial = async <T>(
  scmAuth: ScmAuthApi,
  query: string,
  variables: Record<string, unknown>,
): Promise<{ data: T; errors: GraphqlError[] }> => {
  const { token } = await scmAuth.getCredentials({
    url: GITHUB_URL,
    additionalScope: { customScopes: { github: ['repo'] } },
    optional: true,
  });
  if (!token) throw new Error('Not signed in to GitHub');
  const response = await fetch(GRAPHQL_URL, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, variables }),
  });
  if (!response.ok) throw new Error(`GitHub answered ${response.status}`);
  const body: { data?: T; errors?: GraphqlError[] } = await response.json();
  // No data, or nothing but nulls at the top: the query itself failed.
  const data = body.data as Record<string, unknown> | undefined;
  if (!data || Object.values(data).every(value => value === null)) {
    throw new Error(
      body.errors?.[0]?.message ?? 'GitHub answered without data',
    );
  }
  return { data: body.data as T, errors: body.errors ?? [] };
};

export const githubGraphql = async <T>(
  scmAuth: ScmAuthApi,
  query: string,
  variables: Record<string, unknown>,
): Promise<T> =>
  (await githubGraphqlPartial<T>(scmAuth, query, variables)).data;
